import { createContext, useContext } from 'react';

type CspNone = `'none'`;
type CspHttps = `https://${string}`;
type CspSelf = `'self'`;
type CspBase = CspNone | CspHttps | CspSelf;
type CspUnsafeInline = `'unsafe-inline'`;
type CspNonce = `'nonce-${string}'`;
type CspHash = Promise<`'sha256-${string}'`>;
type CspWasmUnsafeEval = `'wasm-unsafe-eval'`;

export type CspMap = {
    'script-src': CspBase | CspUnsafeInline | CspNonce | CspHash | CspWasmUnsafeEval;
    'style-src': CspBase | CspUnsafeInline | CspNonce | CspHash;
    'font-src': CspBase;
    'connect-src': CspBase;
    'frame-src': CspBase;
    'frame-ancestors': CspBase;
    'object-src': CspBase;
    'worker-src': CspBase;
};

export interface HeaderContext {
    addLink(rel: 'preload' | 'modulepreload' | 'preconnect' | 'dns-prefetch', href: string, props?: Partial<Record<string, string>>): void;
    addCsp<T extends keyof CspMap>(type: T, value: CspMap[T]): void;
}

export const HeaderContext = createContext<HeaderContext | undefined>(undefined);

export function preloadStyle(url: string) {
    if (typeof window === 'undefined') useContext(HeaderContext)?.addLink('preload', url, { as: 'style' });
}

export function preloadScript(url: string) {
    if (typeof window === 'undefined') useContext(HeaderContext)?.addLink('modulepreload', url, { as: 'script' });
}

export function preloadFont(url: string) {
    if (typeof window === 'undefined')
        useContext(HeaderContext)?.addLink('preload', url, { as: 'font', type: 'font/woff2', crossorigin: 'anonymous' });
}

export function preloadImage(url: string, type?: string, fetchpriority?: 'high') {
    if (typeof window === 'undefined') useContext(HeaderContext)?.addLink('preload', url, { as: 'image', type, fetchpriority });
}

export function preconnect(url: string) {
    if (typeof window === 'undefined') useContext(HeaderContext)?.addLink('preconnect', url);
}

const digestCache = new Map<string, Promise<`'sha256-${string}'`>>();
export function includeScriptHashInCsp(script: string) {
    if (typeof window === 'undefined') useContext(HeaderContext)?.addCsp('script-src', digestCache.getOrInsertComputed(script, digest));
}

export function includeInCsp<T extends keyof CspMap>(type: T, value: CspMap[T]) {
    if (typeof window === 'undefined') useContext(HeaderContext)?.addCsp(type, value);
}

async function digest(message: string): Promise<`'sha256-${string}'`> {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return `'sha256-${new Uint8Array(hash).toBase64()}'`;
}
